import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';

import { VoltaIconName } from '../icons/volta-icons';

/** Named sizes, in px. Mirrors the icon scale in the Volta UI spec. */
const SIZE_MAP = {
  small: 14, // trailing icons inside inputs
  medium: 16, // dense inline icons
  nav: 18, // sidebar / navigation icons
  large: 20, // buttons, action bar
  xlarge: 26, // page header
} as const;

export type UiIconSize = keyof typeof SIZE_MAP;

/**
 * The Volta icon primitive, backed by Lucide.
 *
 * Wrapping `lucide-icon` here (rather than using it directly in templates) keeps
 * the `ui-` design-system prefix, pins sizes to the spec's icon scale, and means
 * the underlying icon library can be swapped without touching feature templates.
 *
 * Icons inherit `currentColor`, so colour them by setting `color` on the parent —
 * no filter/hue-rotate hacks needed.
 *
 *   <ui-icon name="ShoppingBag"></ui-icon>
 *   <ui-icon name="chevron-down" size="small"></ui-icon>
 */
@Component({
  selector: 'ui-icon',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './icon.component.html',
  styleUrls: ['./icon.component.scss'],
})
export class IconComponent {
  /** Lucide icon name — PascalCase or kebab-case. Must be picked in VOLTA_ICONS. */
  @Input() name!: VoltaIconName | string;

  @Input() size: UiIconSize = 'nav';

  /** Explicit px size. Overrides `size` when set. */
  @Input() pxSize?: number;

  /**
   * Default 1.75 rather than Lucide's 2 — it sits better next to the 500-weight
   * nav labels. Selected nav rows bump this to 2 in CSS to match their 600 text.
   */
  @Input() strokeWidth = 1.75;

  /**
   * Left off by default so `strokeWidth` maps straight to the rendered
   * `stroke-width` attribute. That keeps the CSS overrides used for selected
   * states honest — with absolute scaling on, the attribute is multiplied by
   * 24/size and a flat CSS value would silently mean something else.
   */
  @Input() absoluteStrokeWidth = false;

  get pixelSize(): number {
    return this.pxSize ?? SIZE_MAP[this.size] ?? SIZE_MAP.nav;
  }
}
